import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { Link } from "react-router-dom";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import { resolveMenuIcon } from "./iconRegistry";

const DEPTH_PADDING = ["pl-3", "pl-7", "pl-11", "pl-14", "pl-[4.2rem]", "pl-[5.2rem]"];
const DEPTH_LINE = ["left-[0.55rem]", "left-[1.55rem]", "left-[2.5rem]", "left-[3.3rem]", "left-[4.1rem]", "left-[5rem]"];

/**
 * Una fila del menú. No sabe nada de árboles ni de recursión: recibe todo
 * ya resuelto (si está activa, si tiene hijos abiertos, etc.) y solo se
 * encarga de pintarse y de avisar clicks. Se reutiliza tanto en el render
 * en árbol normal como en el modo virtualizado (lista plana).
 */
export function MenuItemRow({
  item,
  depth = 0,
  compact = false,
  isOpen = false,
  isCurrentRouteActive = false,
  isChildRouteActive = false,
  isFavorite = false,
  onToggleFavorite,
  onSelect,
}) {
  const hasChildren = item.children && item.children.length > 0;
  const isActive = isCurrentRouteActive || isChildRouteActive;
  const targetRoute = item.route || undefined;
  const Icon = resolveMenuIcon(item.icon);

  const itemClassName = [
    "group relative mx-1.5 my-[2px] min-h-[38px] rounded-lg transition-all duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-morado focus-visible:ring-offset-1",
    isActive
      ? "bg-morado/12 ring-1 ring-morado/25 shadow-sm dark:bg-morado/25 dark:ring-morado/40"
      : "hover:bg-slate-100/90 dark:hover:bg-white/10",
  ].join(" ");

  const textClassName = [
    "truncate text-[12.5px] leading-5",
    isCurrentRouteActive
      ? "font-bold text-morado dark:text-white"
      : isChildRouteActive
      ? "font-semibold text-slate-700 dark:text-slate-100"
      : "font-medium text-slate-700 dark:text-slate-200",
  ].join(" ");

  const iconContainerClassName = [
    "flex h-7 w-7 rounded-lg items-center justify-center transition-colors duration-200",
    isActive
      ? "bg-white text-morado shadow-[0_1px_2px_rgba(15,23,42,0.08)] dark:bg-white/90"
      : "bg-slate-100 text-slate-500 group-hover:text-morado dark:bg-white/10 dark:text-slate-300",
  ].join(" ");

  const row = (
    <ListItem disablePadding className="relative">
      <ListItemButton
        component={!hasChildren && targetRoute ? Link : "button"}
        to={!hasChildren && targetRoute ? targetRoute : undefined}
        onClick={() => onSelect(item)}
        aria-current={isCurrentRouteActive ? "page" : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
        className={`${itemClassName} ${compact ? "justify-center pl-0 pr-0" : `${DEPTH_PADDING[Math.min(depth, 5)]} pr-2`}`}
      >
        {!compact && depth > 0 && (
          <span
            className={`absolute top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full bg-slate-200 dark:bg-white/15 ${DEPTH_LINE[Math.min(depth, 5)]}`}
          />
        )}

        <ListItemIcon className={compact ? "!min-w-0 !mr-0 justify-center" : "!min-w-7"}>
          <span className={iconContainerClassName}>
            <Icon fontSize="small" />
          </span>
        </ListItemIcon>

        {!compact && (
          <>
            <ListItemText primary={<span className={textClassName}>{item.name}</span>} />

            {Boolean(item.badgeCount) && (
              <span className="ml-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-naranja px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                {item.badgeCount > 99 ? "99+" : item.badgeCount}
              </span>
            )}

            {!hasChildren && item.route && (
              <button
                type="button"
                aria-label={isFavorite ? `Quitar ${item.name} de favoritos` : `Agregar ${item.name} a favoritos`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onToggleFavorite(item.id);
                }}
                className={`ml-1 flex h-6 w-6 items-center justify-center rounded-md transition-opacity duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-morado ${
                  isFavorite ? "opacity-100 text-amber-500" : "opacity-0 group-hover:opacity-100 text-slate-300 hover:text-amber-500 dark:text-slate-500"
                }`}
              >
                {isFavorite ? <StarRoundedIcon fontSize="small" /> : <StarBorderRoundedIcon fontSize="small" />}
              </button>
            )}

            {hasChildren && (
              <ExpandMoreRoundedIcon
                className={[
                  "rounded-full border border-morado/15 bg-morado/10 p-[1px] text-[17px] text-morado transition-transform duration-200 dark:border-white/20 dark:bg-white/10 dark:text-white",
                  isOpen ? "rotate-0" : "-rotate-90",
                ].join(" ")}
              />
            )}
          </>
        )}
      </ListItemButton>
    </ListItem>
  );

  if (!compact) return row;

  return (
    <Tooltip title={item.name} placement="right" arrow>
      <span>{row}</span>
    </Tooltip>
  );
}
